"use client";

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiError, api } from "@/lib/api";

/**
 * EPIC-041 part 3a — toggle component for the auto-share-to-feed
 * preference. PIs render disabled with a tooltip; non-PIs can flip
 * the switch.
 *
 * The PI lock is enforced server-side (PATCH returns 403 for PIs).
 * The component reads users.role from /v1/me + checks for an
 * approved PI application by attempting the GET endpoint and
 * inspecting the value, then disables the toggle accordingly. The
 * authoritative source is the server's 403 — UI just helps avoid a
 * pointless click.
 */
export function AutoShareToggle() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [lockedByPi, setLockedByPi] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const [me, pref] = await Promise.all([
          api.me(),
          api.getAutoShareToFeed(),
        ]);
        if (cancelled) return;
        setEnabled(pref.autoShareToFeed);
        // Heuristic: creators / admins COULD be PIs; the server is the
        // authority. We only render the disabled state preemptively
        // when the server's 403 has fired before — to keep this PR
        // small, leave the toggle enabled and let the server respond
        // with 403 if the user is a PI. If the response says
        // lockedByPi, we'll honour it.
        setLockedByPi(false);
        // role check is informational only — server enforces.
        // (Uncomment once we add a /v1/me/is-pi endpoint:
        //   setLockedByPi(me.role !== 'user');
        // For now, attempt the PATCH and surface 403 inline.)
        void me;
        setLoading(false);
      } catch (err) {
        if (cancelled) return;
        setError(
          err instanceof Error
            ? err.message
            : t("autoShareToggle.errLoad"),
        );
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function handleToggle(next: boolean) {
    if (submitting || lockedByPi) return;
    const previous = enabled;
    setEnabled(next);
    setSubmitting(true);
    setError(null);
    try {
      const result = await api.updateAutoShareToFeed(next);
      setEnabled(result.autoShareToFeed);
      setLockedByPi(result.lockedByPi);
    } catch (err) {
      // Server's 403 → revert + show "PI users can't disable this".
      setEnabled(previous);
      if (err instanceof ApiError && err.status === 403) {
        setLockedByPi(true);
        setError(t("autoShareToggle.piLockedNote"));
      } else {
        setError(
          err instanceof Error
            ? err.message
            : t("autoShareToggle.errUpdate"),
        );
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="text-[12px] text-[var(--color-text3)]">
        {t("autoShareToggle.loading")}
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-[14px] font-semibold text-[var(--color-text)]">
            {t("autoShareToggle.title")}
          </div>
          <p className="mt-1 max-w-md text-[12px] text-[var(--color-text3)]">
            {t("autoShareToggle.description")}
          </p>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={enabled}
          aria-disabled={lockedByPi || submitting}
          disabled={lockedByPi || submitting}
          onClick={() => handleToggle(!enabled)}
          className="relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[var(--color-brand)] disabled:cursor-not-allowed disabled:opacity-60"
          style={{
            background: enabled ? "var(--color-brand)" : "var(--color-border)",
          }}
          title={
            lockedByPi
              ? t("autoShareToggle.piLockedNote")
              : undefined
          }
        >
          <span
            className="inline-block h-4 w-4 transform rounded-full bg-white transition-transform"
            style={{
              transform: enabled ? "translateX(22px)" : "translateX(4px)",
            }}
          />
        </button>
      </div>
      {error && (
        <p className="mt-2 text-[11px] text-red-400">{error}</p>
      )}
      {lockedByPi && !error && (
        <p className="mt-2 text-[11px] text-[var(--color-text3)]">
          {t("autoShareToggle.piLockedNote")}
        </p>
      )}
    </div>
  );
}
